/**
 * NotificationsBell — topbar control that polls `list-notifications` for
 * the unread count and the most recent items, per
 * `docs/frontend/035-frontend-app-shell-plan.md §3.5`.
 *
 * MVP behaviour:
 *   - unread badge from `list-notifications?limit=20&unread_only=true` on
 *     mount (does not poll faster than 30s — we use 60s);
 *   - quick view dropdown shows the top 5 unread + a "view all" link to
 *     `/app/notifications`;
 *   - mark-read / dismiss actions are deferred (mutation endpoints not
 *     built).
 *
 * Task 310 §2 — the poll moved into `notificationsPolling.ts`. It is now
 * visibility-aware (a hidden tab polls nothing and aborts what is in
 * flight), single-flight, abortable, backed off on consecutive failures,
 * and gated on a ready operational tenant session. The healthy cadence is
 * still 60s and nothing about the rendered control changed.
 */

import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { listNotifications } from '../../api/endpoints';
import { useMeContext } from '../../context/MeContextProvider';
import { BellIcon } from './icons';
import {
  createNotificationsPoller,
  emptyNotificationsSnapshot,
  notificationsPollGate,
  reduceNotificationsSnapshot,
  type NotificationsSnapshot,
} from './notificationsPolling';

export function NotificationsBell() {
  const { status, me } = useMeContext();
  const { shouldPoll, identityKey } = notificationsPollGate(status, me);
  const [snapshot, setSnapshot] = useState<NotificationsSnapshot>(() =>
    emptyNotificationsSnapshot(identityKey),
  );
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // No poller at all outside a ready operational tenant session. The
    // identity is still known while `status` is `loading` (a manual
    // `refresh()`), so the badge keeps its count while the poll pauses.
    if (!shouldPoll) return;
    const poller = createNotificationsPoller({
      now: () => Date.now(),
      setTimer: (fn, delayMs) => window.setTimeout(fn, delayMs),
      clearTimer: (handle) => window.clearTimeout(handle),
      isVisible: () => document.visibilityState !== 'hidden',
      fetchUnread: async (signal) => {
        const data = await listNotifications({ limit: 20, unreadOnly: true, signal });
        return data.notifications;
      },
      onOutcome: (outcome) => {
        setSnapshot((current) => reduceNotificationsSnapshot(current, identityKey, outcome));
      },
    });
    const onVisibilityChange = (): void => poller.notifyVisibilityChanged();
    document.addEventListener('visibilitychange', onVisibilityChange);
    poller.start();
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      poller.dispose();
    };
  }, [shouldPoll, identityKey]);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!ref.current) return;
      if (e.target instanceof Node && !ref.current.contains(e.target)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Task 310 §2.5 — the first of the two identity guards. A snapshot that
  // belongs to a different user / organization / supervised subject is not
  // rendered at all, so no previous identity's count or item can survive
  // an identity change even for one paint.
  const shown =
    snapshot.identityKey === identityKey ? snapshot : emptyNotificationsSnapshot(identityKey);
  const visibleCount = shown.unread.length;
  const top = shown.unread.slice(0, 5);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        className="icon-btn"
        title="الإشعارات"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <BellIcon className="" />
        {visibleCount > 0 ? (
          <span className="nb">{visibleCount > 99 ? '99+' : visibleCount}</span>
        ) : null}
      </button>
      {open ? (
        <div
          role="dialog"
          aria-label="الإشعارات"
          style={{
            position: 'absolute',
            top: '100%',
            insetInlineEnd: 0,
            marginTop: 8,
            width: 380,
            maxWidth: 'calc(100vw - 24px)',
            background: 'var(--paper)',
            border: '1px solid var(--line-2)',
            borderRadius: 'var(--r-md)',
            boxShadow: 'var(--sh-pop)',
            zIndex: 'var(--z-drawer)',
          }}
        >
          <div
            style={{
              padding: '14px 16px',
              borderBottom: '1px solid var(--line)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <span className="h-section" style={{ fontSize: 15 }}>
              الإشعارات
            </span>
            <span className="tiny" style={{ color: 'var(--muted)' }}>
              {visibleCount > 0 ? `${visibleCount} غير مقروء` : 'لا جديد'}
            </span>
          </div>
          {shown.failed ? (
            <div className="tiny" style={{ padding: 16, color: 'var(--rose-ink)' }}>
              تعذر تحميل الإشعارات.
            </div>
          ) : top.length === 0 ? (
            <div className="tiny" style={{ padding: 16, color: 'var(--muted)' }}>
              لا توجد إشعارات غير مقروءة.
            </div>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {top.map((n) => (
                <li
                  key={n.id}
                  style={{
                    padding: '12px 16px',
                    borderBottom: '1px solid var(--line)',
                    background: 'var(--paper-2)',
                  }}
                >
                  <div style={{ fontWeight: 600, color: 'var(--ink)', fontSize: 12 }}>
                    {n.title}
                  </div>
                  {n.body ? (
                    <div
                      className="tiny"
                      style={{ color: 'var(--ink-2)', marginTop: 3, lineHeight: 1.5 }}
                    >
                      {n.body}
                    </div>
                  ) : null}
                  <div className="tiny" style={{ color: 'var(--muted)', marginTop: 4 }}>
                    {n.notification_type} · {new Date(n.created_at).toLocaleString('ar-SA')}
                  </div>
                </li>
              ))}
            </ul>
          )}
          <div
            style={{
              padding: 10,
              borderTop: '1px solid var(--line)',
              textAlign: 'center',
            }}
          >
            <Link
              to="/app/notifications"
              onClick={() => setOpen(false)}
              style={{ fontSize: 12, fontWeight: 600 }}
            >
              عرض كل الإشعارات →
            </Link>
          </div>
        </div>
      ) : null}
    </div>
  );
}
